import React, { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/**
 * Searchable dropdown (type-to-filter) over a flat list of {id, name} options.
 * Strict selection only — value must come from `options`, but a current
 * `value` not present in `options` (e.g. legacy free-text data) is still
 * shown and selectable so existing data isn't lost.
 */
export default function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = 'Select...',
  disabled = false,
  className,
  'data-testid': dataTestId,
}) {
  const [open, setOpen] = useState(false);

  const allOptions = value && !options.some(o => o.name === value)
    ? [...options, { id: `legacy-${value}`, name: value }]
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal h-9', className)}
          data-testid={dataTestId}
        >
          <span className="truncate text-left">{value || <span className="text-muted-foreground">{placeholder}</span>}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {allOptions.map((opt) => (
                <CommandItem
                  key={opt.id}
                  value={opt.name}
                  onSelect={() => { onValueChange(opt.name); setOpen(false); }}
                  className="cursor-pointer"
                >
                  <Check className={cn('mr-2 h-4 w-4', value === opt.name ? 'opacity-100' : 'opacity-0')} />
                  {opt.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
